import React, { useEffect, useRef } from 'react';

const FLEE_RADIUS = 110;
const MAX_ROTATE = 22;

/**
 * Playful horizontal dodge + roll away from the cursor inside a bounds element.
 * @param {{ children: React.ReactNode, boundsRef: React.RefObject<HTMLElement | null>, className?: string, edgePadding?: number }} props
 */
export default function CursorShyHorizontal({ children, boundsRef, className = '', edgePadding = 4 }) {
  const floatRef = useRef(null);
  const physicsRef = useRef({
    x: 0,
    rot: 0,
    vx: 0,
    vRot: 0
  });
  const targetRef = useRef({ x: 0, rot: 0 });
  const proximityRef = useRef(0);
  const returningRef = useRef(false);
  const rafRef = useRef(0);
  const lastFrameRef = useRef(0);

  useEffect(() => {
    const prefersReduced =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) return undefined;

    const onMove = (e) => {
      const bounds = boundsRef.current;
      const floatEl = floatRef.current;
      if (!bounds || !floatEl) return;

      const boundsRect = bounds.getBoundingClientRect();
      const floatRect = floatEl.getBoundingClientRect();
      const floatCenterX = floatRect.left + floatRect.width / 2;
      const floatCenterY = floatRect.top + floatRect.height / 2;

      const padY = 48;
      if (
        e.clientY < boundsRect.top - padY ||
        e.clientY > boundsRect.bottom + padY ||
        e.clientX < boundsRect.left - padY ||
        e.clientX > boundsRect.right + padY
      ) {
        proximityRef.current = 0;
        returningRef.current = true;
        targetRef.current.x = 0;
        targetRef.current.rot = 0;
        return;
      }

      const dx = floatCenterX - e.clientX;
      const dy = floatCenterY - e.clientY;
      const dist = Math.hypot(dx, dy);

      if (dist < FLEE_RADIUS && dist > 0.5) {
        returningRef.current = false;
        const proximity = 1 - dist / FLEE_RADIUS;
        proximityRef.current = proximity;
        const push = proximity ** 2.2;
        const horizontal = dx / dist;
        const maxOffset = Math.max(
          16,
          (boundsRect.width - floatRect.width) / 2 - edgePadding
        );
        const pushStrength = 14 + proximity * 58;
        const nextX = physicsRef.current.x + horizontal * push * pushStrength;
        targetRef.current.x = Math.max(-maxOffset, Math.min(maxOffset, nextX));
        targetRef.current.rot = Math.max(
          -MAX_ROTATE,
          Math.min(MAX_ROTATE, -targetRef.current.x * (0.14 + proximity * 0.14))
        );
      } else {
        proximityRef.current = 0;
        returningRef.current = true;
        targetRef.current.x = 0;
        targetRef.current.rot = 0;
      }
    };

    const tick = (now) => {
      if (!lastFrameRef.current) lastFrameRef.current = now;
      const dt = Math.min(2.4, (now - lastFrameRef.current) / 16.67);
      lastFrameRef.current = now;

      const body = physicsRef.current;
      const target = targetRef.current;
      const fleeing = proximityRef.current > 0.1;
      const returning = returningRef.current && !fleeing;

      const stiffness = fleeing ? 0.19 : 0.1;
      const damping = fleeing ? 0.74 : 0.71;
      const rotStiffness = fleeing ? 0.13 : 0.055;
      const rotDamping = fleeing ? 0.7 : 0.66;

      const ax = (target.x - body.x) * stiffness;
      body.vx = (body.vx + ax * dt) * damping;
      body.x += body.vx * dt;

      let rotTarget = target.rot;
      if (returning) {
        const pendulum = -body.x * 0.24 - body.vx * 0.42;
        rotTarget = Math.max(-MAX_ROTATE, Math.min(MAX_ROTATE, pendulum));
      }

      const aRot = (rotTarget - body.rot) * rotStiffness;
      body.vRot = (body.vRot + aRot * dt) * rotDamping;
      body.rot += body.vRot * dt;

      if (returning) {
        const settled =
          Math.abs(body.x) < 0.45 &&
          Math.abs(body.vx) < 0.06 &&
          Math.abs(body.rot) < 0.35 &&
          Math.abs(body.vRot) < 0.05;
        if (settled) {
          body.x = 0;
          body.rot = 0;
          body.vx = 0;
          body.vRot = 0;
          returningRef.current = false;
        }
      }

      const el = floatRef.current;
      if (el) {
        el.style.transform = `translate(-50%, -50%) translateX(${body.x}px) rotate(${body.rot}deg)`;
      }
      rafRef.current = window.requestAnimationFrame(tick);
    };

    window.addEventListener('mousemove', onMove, { passive: true });
    rafRef.current = window.requestAnimationFrame(tick);

    return () => {
      window.removeEventListener('mousemove', onMove);
      window.cancelAnimationFrame(rafRef.current);
      lastFrameRef.current = 0;
    };
  }, [boundsRef, edgePadding]);

  return (
    <div
      ref={floatRef}
      className={`absolute left-1/2 top-1/2 will-change-transform pointer-events-none ${className}`}
      style={{ transform: 'translate(-50%, -50%)' }}
    >
      {children}
    </div>
  );
};
